import * as XLSX from 'xlsx';

const COL_MARCAR = 'Marcar (SI/NO)';
const COL_DNI = 'DNI';
const COL_NOMBRE = 'Nombre completo';
const COL_CARGO = 'Cargo';
const COL_PROYECTO = 'Proyecto';
const COL_ESTADO = 'Estado SCTR';
const COL_ID = 'ID empleado (no modificar)';

const VALORES_SI = new Set(['si', 'sí', 's', '1', 'x', 'true', 'yes', 'y']);

function normalizarDni(valor) {
  return String(valor ?? '')
    .trim()
    .replace(/\s+/g, '');
}

function marcarEsSi(valor) {
  const v = String(valor ?? '')
    .trim()
    .toLowerCase();
  return VALORES_SI.has(v);
}

function textoEstadoSctr(ficha) {
  const estado = String(ficha?.sctr?.estado || 'sin');
  if (estado === 'sin') return 'Sin SCTR';
  if (estado === 'vencido') return 'Vencido';
  if (estado === 'por_vencer') return 'Por vencer';
  if (estado === 'vigente') return 'Vigente';
  return estado;
}

function descargarBlob(buffer, nombreArchivo) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombreArchivo);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * @param {Array} fichas Lista de fichas SSOMA (empleados)
 * @param {{ soloActivos?: boolean, nombreEmpresa?: string }} opciones
 */
export function descargarPlantillaSctrMasivo(fichas, opciones = {}) {
  const { soloActivos = true, nombreEmpresa = 'empresa' } = opciones;
  let lista = fichas || [];
  if (soloActivos) {
    lista = lista.filter((f) => String(f.estado || '').toLowerCase() === 'activo');
  }
  lista = [...lista].sort((a, b) =>
    String(a.nombre_completo || '').localeCompare(String(b.nombre_completo || ''), 'es')
  );

  const filas = lista.map((f) => ({
    [COL_MARCAR]: 'NO',
    [COL_DNI]: f.numero_documento || '',
    [COL_NOMBRE]: f.nombre_completo || '',
    [COL_CARGO]: f.cargo || '',
    [COL_PROYECTO]: f.proyecto || '',
    [COL_ESTADO]: textoEstadoSctr(f),
    [COL_ID]: f.id,
  }));

  const hoja = XLSX.utils.json_to_sheet(filas, {
    header: [COL_MARCAR, COL_DNI, COL_NOMBRE, COL_CARGO, COL_PROYECTO, COL_ESTADO, COL_ID],
  });
  hoja['!cols'] = [
    { wch: 14 },
    { wch: 12 },
    { wch: 36 },
    { wch: 22 },
    { wch: 18 },
    { wch: 14 },
    { wch: 22 },
  ];

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, 'SCTR masivo');
  const instrucciones = XLSX.utils.aoa_to_sheet([
    ['Instrucciones'],
    ['1. En la columna "Marcar (SI/NO)" escriba SI para incluir al trabajador en la carga masiva.'],
    ['2. No modifique DNI ni ID empleado.'],
    ['3. Guarde el archivo y use "Importar selección" en el módulo SSOMA → SCTR.'],
  ]);
  XLSX.utils.book_append_sheet(libro, instrucciones, 'Ayuda');

  const buffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
  const slug = String(nombreEmpresa)
    .replace(/[^\w\-]+/g, '_')
    .slice(0, 40);
  const fecha = new Date().toISOString().slice(0, 10);
  descargarBlob(buffer, `plantilla_sctr_masivo_${slug}_${fecha}.xlsx`);
}

/**
 * @param {File} archivo
 * @param {Array} fichas Todas las fichas de la empresa (para validar IDs/DNI)
 */
export async function importarSeleccionSctrMasivoDesdeExcel(archivo, fichas) {
  const porId = new Map((fichas || []).map((f) => [String(f.id), f]));
  const porDni = new Map();
  (fichas || []).forEach((f) => {
    const dni = normalizarDni(f.numero_documento);
    if (dni) porDni.set(dni, f);
  });

  const buffer = await archivo.arrayBuffer();
  const libro = XLSX.read(buffer, { type: 'array' });
  const nombreHoja = libro.SheetNames.find((n) => n.toLowerCase().includes('sctr')) || libro.SheetNames[0];
  const hoja = libro.Sheets[nombreHoja];
  const filas = XLSX.utils.sheet_to_json(hoja, { defval: '' });

  const ids = [];
  const idsSet = new Set();
  let filasMarcadas = 0;
  let filasSinMatch = 0;
  let filasSinMarca = 0;

  filas.forEach((fila) => {
    const marcarRaw =
      fila[COL_MARCAR] ??
      fila.Marcar ??
      fila['Marcar (SI/NO)'] ??
      fila.marcar ??
      '';
    if (!marcarEsSi(marcarRaw)) {
      filasSinMarca += 1;
      return;
    }
    filasMarcadas += 1;

    const idRaw = fila[COL_ID] ?? fila['ID empleado (no modificar)'] ?? fila.id ?? fila.ID ?? '';
    const dniRaw = fila[COL_DNI] ?? fila.DNI ?? fila.dni ?? '';

    let ficha = null;
    if (idRaw !== '' && idRaw != null) {
      ficha = porId.get(String(idRaw).trim());
    }
    if (!ficha) {
      const dni = normalizarDni(dniRaw);
      if (dni) ficha = porDni.get(dni);
    }

    if (!ficha) {
      filasSinMatch += 1;
      return;
    }
    const key = String(ficha.id);
    if (!idsSet.has(key)) {
      idsSet.add(key);
      ids.push(key);
    }
  });

  return {
    ids,
    filasMarcadas,
    filasSinMatch,
    filasLeidas: filas.length,
    filasSinMarca,
  };
}
